import { cloneElement } from "react";
import { Box, useTheme } from "@mui/material";

// Drawn on a 40×40 grid: a softly scalloped disc centred at (20, 16), radius
// ~14, with two plain ribbon tails behind it, all in the primary colour
const CENTRE = { x: 20, y: 16 };
// Few, shallow scallops: enough to read as a rosette, quiet enough to sit
// beside the app's flat icons. Each scallop is a peak and a dip.
const SCALLOPS = 8;
const POINTS = SCALLOPS * 2;
const OUTER = 14;
const INNER = 13;

// The disc's edge: alternating radii, softened by a round-joined stroke in
// the same colour, which reads as scallops
const ROSETTE = Array.from({ length: POINTS }, (_, i) => {
  const angle = (i / POINTS) * Math.PI * 2;
  const radius = i % 2 === 0 ? OUTER : INNER;
  return `${(CENTRE.x + radius * Math.sin(angle)).toFixed(2)},${(
    CENTRE.y -
    radius * Math.cos(angle)
  ).toFixed(2)}`;
}).join(" ");

// Two plain slanted strips, square-ended
const TAILS = ["14,24 10,37 15,37 19,26", "26,24 30,37 25,37 21,26"];
const TAIL_OPACITY = 0.55; // the disc's own colour, faded

/**
 * An unlocked achievement's icon on a rosette, for the unlock toast — the
 * moment of earning it, whatever its kind (a badge level now; later a
 * completed goal or list). Dashboard rows keep plain icons. `icon` is an MUI
 * icon element, centred on the disc. `size` is the overall square in px.
 */
export default function AchievementBadge({ icon, size = 36 }) {
  const { palette } = useTheme();
  const colour = palette.primary.main;

  return (
    <Box
      sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}
    >
      <svg viewBox="0 0 40 40" width={size} height={size} aria-hidden="true">
        {TAILS.map((points) => (
          <polygon
            key={points}
            points={points}
            fill={colour}
            fillOpacity={TAIL_OPACITY}
          />
        ))}
        <polygon
          points={ROSETTE}
          fill={colour}
          stroke={colour}
          strokeWidth={2}
          strokeLinejoin="round"
        />
      </svg>
      {/* The icon over the disc's centre (40% down the square, above the tails) */}
      {icon && (
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: `${(CENTRE.y / 40) * 100}%`,
            transform: "translate(-50%, -50%)",
            display: "flex",
            color: palette.primary.contrastText,
          }}
        >
          {cloneElement(icon, { sx: { fontSize: size * 0.38 } })}
        </Box>
      )}
    </Box>
  );
}
